import type { DealStructuredIntakeSectionDto, UpdateDealStructuredIntakeSectionRequest } from "./intakeProfile";

// Typed contracts mirroring the C# DTOs in
// src/PhoenixCapital.Core/DTOs/Deals/DealDtos.cs and the enums in
// src/PhoenixCapital.Core/Enums/DealPipelineEnums.cs.
//
// The API serializes enums as strings (Program.cs — JsonStringEnumConverter),
// so each enum below is declared as a string-literal union matching the
// member names exactly. Field shape and casing match the .NET record.

// ============================== Enums =================================

export type DealPipelineStage =
  | "Intake"
  | "PreApproval"
  | "Underwriting"
  | "Closing"
  | "Closed"
  | "Declined"
  | "Withdrawn";

export type DealSubState =
  // Intake
  | "New"
  | "GatheringParties"
  | "IntakeReview"
  | "ReadyForAssignment"
  | "AssignedToB1"
  // PreApproval — derived from Pre-Approval milestones (Deal Pipeline spec §2.3)
  // (applications, letters, document requests, final submission).
  | "Applications"
  | "Letters"
  | "Documents"
  | "ReadyToSubmit"
  // Underwriting entry sub-state
  | "AssignedToA1"
  // Terminal
  | "DeclinedFinal"
  | "WithdrawnFinal"
  | "ArchivedFinal";

export type CounterpartyKind = "Person" | "Entity" | "Business";

// Broker New Deal Submission — whether real estate is part of the deal.
export type RealEstateIncluded = "None" | "Owned" | "Leased";

// Deal Decline Workflow: the proposal and ratification state. Confirming a
// decline also moves the deal to stage Declined (Q8, 2026-10-12).
export type DealDeclineStatus = "Active" | "DeclineProposed" | "Declined";

// Pipeline list filter, bound to the ?status= query param.
export type DealDeclineFilter =
  | "active"
  | "declineProposed"
  | "declined"
  | "all";

export type TransactionStatus =
  | "PendingLOI"
  | "LOISigned"
  | "PASigned"
  | "InClosing"
  | "Other";

export type ChecklistStatus = "NotStarted" | "InProgress" | "Complete";
export type ChecklistItemStatus =
  | "NotStarted"
  | "InProgress"
  | "Complete"
  | "NotApplicable"
  // TrackedItemRow (Deal Pipeline spec Appendix A) — escalated to the configured executive.
  | "Escalated";

export type PreApprovalRequestStatus =
  | "Draft"
  | "Requested"
  | "Reminded"
  | "Received"
  | "UnderReview"
  | "Approved"
  | "Rejected"
  | "Waived"
  // TrackedItemRow (Deal Pipeline spec Appendix A) — escalated to the configured executive.
  | "Escalated";

// Deal Pipeline spec §6.4 — popup-friendly subset of PreApprovalRequestStatus.
// "Reminded" and "UnderReview" remain in the schema but are not exposed as
// attestation targets; they re-surface when the email channel ships.
export type DocumentRequestAttestStatus =
  | "Requested"
  | "Received"
  | "Approved"
  | "Rejected"
  | "Waived";

export type PreApprovalRequestParty =
  | "Broker"
  | "Borrower"
  | "Seller"
  | "Business"
  | "Multiple";

export type AssignmentRole = "C1" | "B1" | "A1";

export type CertificationRecommendation =
  | "ApproveForUW"
  | "ApproveWithConditions"
  | "DeclineRecommendation";

export type SuggestedCreditRatingPreliminary = "A" | "B" | "C" | "D" | "E" | "TooEarly";

// ============================== Deal ==================================

export interface DealListItemDto {
  id: string;
  dealNumber: string;
  displayName?: string | null;
  stage: DealPipelineStage;
  subState: DealSubState;
  brokerId: string;
  businessNameInitial: string | null;
  purchasePrice: number;
  sellerNoteAmount: number;
  assignedC1Id: string | null;
  assignedB1Id: string | null;
  assignedA1Id: string | null;
  stageEnteredDate: string;
  isArchived: boolean;
  createdAt: string;
  businessId?: string | null;
  // Counterparty sunset — the party FKs ARE the borrower/seller identity.
  borrowerPartyId?: string | null;
  sellerPartyId?: string | null;
  // Decline state for the kanban / table card.
  declineStatus?: DealDeclineStatus;
  declineProposedReason?: string | null;
  declineRatifiedAt?: string | null;
  // Deal Submission spec §6 — deal-structure scalars surfaced
  // as optional/toggleable Pipeline columns.
  cashAtClosing?: number | null;
  subordinatedDebtAmount?: number | null;
  realEstateIncluded?: RealEstateIncluded | null;
  collateralType?: string | null;
  stagingSubmissionId?: string | null;
  assignedC1DisplayName?: string | null;
}

export interface DealDetailDto {
  id: string;
  dealNumber: string;
  displayName?: string | null;
  stage: DealPipelineStage;
  subState: DealSubState;
  brokerId: string;
  businessId: string | null;
  businessNameInitial: string | null;
  naicsInitial: string | null;
  purchasePrice: number;
  sellerNoteAmount: number;
  transactionStatus: TransactionStatus | null;
  targetCloseDate: string | null;
  dealNarrative: string | null;
  // Deal Submission spec §6 — deal-structure scalars.
  cashAtClosing: number | null;
  subordinatedDebtAmount: number | null;
  realEstateIncluded: RealEstateIncluded | null;
  collateralType: string | null;
  assignedC1Id: string | null;
  assignedB1Id: string | null;
  assignedA1Id: string | null;
  c1AssignedDate: string | null;
  b1AssignedDate: string | null;
  a1AssignedDate: string | null;
  intakeChecklistId: string | null;
  preApprovalCertificationId: string | null;
  stageEnteredDate: string;
  declineReasonCode: string | null;
  declineReasonDetail: string | null;
  withdrawalReasonCode: string | null;
  withdrawalReasonDetail: string | null;
  isArchived: boolean;
  archivedDate: string | null;
  archivedBy: string | null;
  loanId: string | null;
  /** Set on a successful Create-Loan call. */
  loanNumber?: string | null;
  closedAt?: string | null;
  clonedFromDealId: string | null;
  createdAt: string;
  modifiedAt: string | null;
  // Deal Decline Workflow. The full
  // decline state is also available via GET .../decline; these fields
  // let the deal-detail banner + readOnly cascade render without a
  // second fetch.
  declineStatus?: DealDeclineStatus;
  declineProposedAt?: string | null;
  declineProposedByUserId?: string | null;
  declineProposedReason?: string | null;
  declineRatifiedAt?: string | null;
  declineRatifiedByUserId?: string | null;
  declineRatifiedNotes?: string | null;
  declineLetterDocumentId?: string | null;
  stagingSubmissionId?: string | null;
  assignedC1DisplayName?: string | null;
  // Counterparty sunset — the party FKs ARE the borrower/seller identity.
  borrowerPartyId?: string | null;
  sellerPartyId?: string | null;
}

// Deal Decline Workflow request / state shapes.

export interface ProposeDeclineRequest {
  reason: string;
}

export interface ConfirmDeclineRequest {
  letterPrintedAndSentAttested: boolean;
  reasonDocumentedAttested: boolean;
  finalIrreversibleAttested: boolean;
  notes: string | null;
  linkedDeclineLetterDocumentId: string | null;
}

export interface DealDeclineStateDto {
  dealId: string;
  status: DealDeclineStatus;
  proposedAt: string | null;
  proposedByUserId: string | null;
  proposedByDisplayName: string | null;
  proposedReason: string | null;
  ratifiedAt: string | null;
  ratifiedByUserId: string | null;
  ratifiedByDisplayName: string | null;
  ratifiedNotes: string | null;
  linkedDeclineLetterDocumentId: string | null;
}

export interface CreateDealRequest {
  brokerId: string;
  purchasePrice: number;
  sellerNoteAmount: number;
  businessNameInitial?: string | null;
  naicsInitial?: string | null;
  transactionStatus?: TransactionStatus | null;
  targetCloseDate?: string | null;
  dealNarrative?: string | null;
  businessId?: string | null;
  // Counterparty sunset — the party FKs ARE the borrower/seller identity
  // (null = attached later on the Detail tab).
  borrowerPartyId?: string | null;
  sellerPartyId?: string | null;
}

export interface UpdateDealRequest {
  purchasePrice: number;
  sellerNoteAmount: number;
  businessNameInitial?: string | null;
  naicsInitial?: string | null;
  transactionStatus?: TransactionStatus | null;
  targetCloseDate?: string | null;
  dealNarrative?: string | null;
  businessId?: string | null;
  // Counterparty sunset — the party FKs ARE the borrower/seller identity.
  borrowerPartyId?: string | null;
  sellerPartyId?: string | null;
  // Deal Submission spec §6 — deal-structure scalars, now
  // editable post-import.
  cashAtClosing?: number | null;
  subordinatedDebtAmount?: number | null;
  realEstateIncluded?: RealEstateIncluded | null;
  collateralType?: string | null;
}

/**
 * Partial deal update (PATCH /api/deals/{id}). Every field is optional and a
 * field left off the body is preserved server-side — unlike UpdateDealRequest
 * (PUT), which is a full replacement that zeroes anything omitted. Use this
 * for targeted edits so unspecified fields aren't destroyed.
 */
export interface PatchDealRequest {
  purchasePrice?: number | null;
  sellerNoteAmount?: number | null;
  businessNameInitial?: string | null;
  naicsInitial?: string | null;
  transactionStatus?: TransactionStatus | null;
  targetCloseDate?: string | null;
  dealNarrative?: string | null;
  businessId?: string | null;
  // Counterparty sunset — party identity; null = leave unchanged (FIND-12).
  borrowerPartyId?: string | null;
  sellerPartyId?: string | null;
  cashAtClosing?: number | null;
  subordinatedDebtAmount?: number | null;
  realEstateIncluded?: RealEstateIncluded | null;
  collateralType?: string | null;
}


export interface DealEditBundleDto {
  deal: DealDetailDto;
  structuredSections: DealStructuredIntakeSectionDto[];
}

export interface UpdateDealEditBundleRequest {
  deal: UpdateDealRequest;
  structuredSections: UpdateDealStructuredIntakeSectionRequest[];
}

export interface DealListQuery {
  page?: number;
  pageSize?: number;
  q?: string;
  stage?: DealPipelineStage[];
  subState?: DealSubState[];
  brokerId?: string;
  personId?: string;
  entityId?: string;
  businessId?: string;
  assignedC1Id?: string;
  assignedB1Id?: string;
  assignedA1Id?: string;
  /**
   * Deal Pipeline spec §5.4 — user filter that matches ANY of the 7 pills
   * (C1/B1/A1/A2/A3/A4/B2). [ASSUMPTION:] the backend filter
   * for /api/deals will be extended to honour `assignedTo`; until then this
   * parameter is ignored server-side and the pipeline returns the un-filtered
   * list. UI continues to render the filter chip so the affordance is
   * visible and the wiring is in place.
   */
  assignedTo?: string;
  hasMissingDocs?: boolean;
  hasOverdueDocs?: boolean;
  daysInStageMin?: number;
  daysInStageMax?: number;
  dateRangeStart?: string;
  dateRangeEnd?: string;
  declineReasonCode?: string;
  withdrawalReasonCode?: string;
  includeArchived?: boolean;
  /** When true, returns ONLY archived deals (overrides includeArchived).
   *  Backs the counterparty Deals tab's "Archived" filter. */
  archivedOnly?: boolean;
  /** Deal Pipeline spec §4.1a — include <c>Stage = Closed</c> deals. Defaults
   *  to false so the Pipeline default-hides converted-to-Loan deals. */
  includeClosed?: boolean;
  /** Decline-state filter. When omitted the list
   *  hides only Declined deals (Active + DeclineProposed stay visible).
   *  The "Show Declined" pipeline toggle flips this to "all". */
  status?: DealDeclineFilter;
  /** Deal Pipeline spec §4.1a — quick filter on the
   *  import-populated RealEstateIncluded scalar. */
  realEstateIncluded?: RealEstateIncluded;
}

// ============== Stage / sub-state / lifecycle requests ===============

export interface AdvanceStageRequest {
  targetStage: DealPipelineStage;
  targetSubState: DealSubState;
  reason?: string | null;
  confirmAdminOverride?: boolean;
  /** Required for a move to Declined, which takes the decline path (owner
   *  ruling 17): a DeclineReasonRef code, with `reason` as its detail. */
  reasonCode?: string | null;
}

export interface AdvanceSubStateRequest {
  targetSubState: DealSubState;
  reason?: string | null;
}

export interface DeclineRequest {
  reasonCode: string;
  detail: string;
}

export interface WithdrawRequest {
  reasonCode: string;
  detail: string;
}

export interface ArchiveRequest {
  reason?: string | null;
}

export interface CloneRequest {
  reason?: string | null;
}

// ============================== Assignment ============================

export interface AssignB1Request {
  assigneeUserId: string;
  reason?: string | null;
}

export interface ReassignRequest {
  assigneeUserId: string;
  reason: string;
}

// ============================== Intake checklist ======================

export interface IntakeChecklistItemDto {
  id: string;
  checklistId: string;
  itemNumber: number;
  title: string;
  description: string | null;
  category: string;
  required: boolean;
  isStandardTemplateItem: boolean;
  status: ChecklistItemStatus;
  assignedTo: string | null;
  notes: string | null;
  completedDate: string | null;
  completedBy: string | null;
  // TrackedItemRow (Deal Pipeline spec Appendix A) — set when the item has been escalated.
  escalationRecordId: string | null;
  // TrackedItemRow hotfix — resolution snapshot of the linked EscalationRecord.
  // Null when the item has never been escalated.
  escalation: TrackedItemEscalationSnapshot | null;
  // LOI interim fix — true when this Required item is eligible for the
  // NotApplicable transition despite being Required (stamped once at
  // checklist-creation time based on the Deal's TransactionStatus).
  allowNotApplicableOverride: boolean;
}

/**
 * TrackedItemRow hotfix — resolution snapshot of the most-recent
 * EscalationRecord linked to a tracked item. `resolutionDirective` and
 * `resolvedAt` are null while `status` is "Pending".
 */
export interface TrackedItemEscalationSnapshot {
  status: "Pending" | "Approved" | "Denied";
  resolutionDirective: string | null;
  resolvedAt: string | null;
}

export interface IntakeChecklistDto {
  id: string;
  dealId: string;
  templateVersion: number;
  overallStatus: ChecklistStatus;
  completedDate: string | null;
  completedBy: string | null;
  items: IntakeChecklistItemDto[];
}

export interface UpsertChecklistItemRequest {
  status: ChecklistItemStatus;
  notes?: string | null;
  assignedTo?: string | null;
}

export interface AddCustomChecklistItemRequest {
  title: string;
  description?: string | null;
  category: string;
  required: boolean;
  assignedTo?: string | null;
}

// ============================== Document requests =====================

export interface PreApprovalDocumentRequestDto {
  id: string;
  dealId: string;
  documentTypeRefCode: string;
  requestedFromParty: PreApprovalRequestParty;
  requestedFromContactRefJson: string | null;
  required: boolean;
  notes: string | null;
  status: PreApprovalRequestStatus;
  reasonCode: string | null;
  requestedDate: string | null;
  dueDate: string;
  remindersSentCount: number;
  lastReminderDate: string | null;
  receivedDate: string | null;
  linkedDocumentIds: string[];
  reviewerId: string | null;
  reviewNotes: string | null;
  decisionDate: string | null;
  // TrackedItemRow (Deal Pipeline spec Appendix A) — set when the request has been escalated.
  escalationRecordId: string | null;
  // TrackedItemRow hotfix — resolution snapshot of the linked EscalationRecord.
  // Null when the request has never been escalated.
  escalation: TrackedItemEscalationSnapshot | null;
}

export interface CreateDocumentRequestRequest {
  documentTypeRefCode: string;
  requestedFromParty: PreApprovalRequestParty;
  required: boolean;
  dueDate?: string | null;
  notes?: string | null;
  requestedFromContactRefJson?: string | null;
}

// Deal Pipeline spec §6.4 — single attestation submit for the popup. Replaces
// the menu of /approve, /reject, /waive, /link-documents calls while the
// email-driven send/remind flow is dark-shipped.
export interface AttestDocumentRequestRequest {
  targetStatus: DocumentRequestAttestStatus;
  notes?: string | null;
  receivedDate?: string | null;
  receivedFromContact?: string | null;
  reasonCode?: string | null;
}

// ============================== Transitions / history ==============

export interface DealStageTransitionDto {
  id: string;
  dealId: string;
  fromStage: DealPipelineStage;
  toStage: DealPipelineStage;
  fromSubState: DealSubState;
  toSubState: DealSubState;
  actorUserId: string;
  transitionReason: string | null;
  durationInPriorStageMinutes: number | null;
  transitionDate: string;
  isAdminOverride: boolean;
}

export interface DealAssignmentHistoryDto {
  id: string;
  dealId: string;
  assignmentRole: AssignmentRole;
  assignedUserId: string;
  previousUserId: string | null;
  assignedByUserId: string;
  reassignmentReason: string | null;
  assignmentDate: string;
}

// ============================== Permission constants ==================

// Centralizes the permission strings the UI gates on. Server-side
// authorization is the source of truth (see the deal controllers + RolePermissionMap);
// these strings only drive button visibility and route gating.
export const DEAL_PERMISSIONS = {
  Create: "Deals.Create",
  Read: "Deals.Read",
  Update: "Deals.Update",
  Delete: "Deals.Delete",
  Assign: "Deals.Assign",
  Reassign: "Deals.Reassign",
  AdvanceStage: "Deals.AdvanceStage",
  Decline: "Deals.Decline",
  Withdraw: "Deals.Withdraw",
  CertifyForUW: "Deals.CertifyForUW",
  Archive: "Deals.Archive",
  Restore: "Deals.Restore",
  ChangeCounterparty: "DealFlow.ChangeCounterparty",
  // TrackedItemRow (Deal Pipeline spec Appendix A) — escalate a stuck intake checklist item.
  EscalateIntakeItem: "DealFlow.EscalateIntakeItem",
} as const;

export type CounterpartyPartyType = "Borrower" | "Seller" | "Business";

export interface ChangeCounterpartyRequest {
  partyType: CounterpartyPartyType;
  /** A Party id for Borrower/Seller; the Business id for the Business role. */
  newPartyId: string;
  reason: string;
}

export const DOCUMENT_REQUEST_PERMISSIONS = {
  Create: "DocumentRequests.Create",
  Read: "DocumentRequests.Read",
  Update: "DocumentRequests.Update",
  Send: "DocumentRequests.Send",
  Receive: "DocumentRequests.Receive",
  Approve: "DocumentRequests.Approve",
  Reject: "DocumentRequests.Reject",
  Waive: "DocumentRequests.Waive",
  // TrackedItemRow (Deal Pipeline spec Appendix A) — escalate a stuck document request.
  Escalate: "DealFlow.EscalateDocumentRequest",
} as const;

// ============================== Pre-Approval milestones ===============
// LA attestation surface on the redesigned Pre-Approval page. Two
// applications (Borrower + Seller), two letters (Borrower + Seller), each
// with three sequential milestones the LA attests to (a letter's approval
// chain also fills a blank one). The page does not
// generate documents and does not integrate with any e-signature
// provider; the optional `linkedDocumentId` is a pointer into the
// Document Repository for navigation only.

export type PreApprovalPartyKind = "Borrower" | "Seller";

export type PreApprovalApplicationStatus =
  | "NotStarted"
  | "Generated"
  | "Sent"
  | "Received";

export type PreApprovalLetterStatus =
  | "NotStarted"
  | "Generated"
  | "Sent"
  | "ReceivedSigned";

export interface PreApprovalApplicationDto {
  id: string;
  dealId: string;
  party: PreApprovalPartyKind;
  status: PreApprovalApplicationStatus;
  generatedAt: string | null;
  generatedByUserId: string | null;
  generatedByDisplayName: string | null;
  sentAt: string | null;
  sentByUserId: string | null;
  sentByDisplayName: string | null;
  receivedAt: string | null;
  receivedByUserId: string | null;
  receivedByDisplayName: string | null;
  linkedDocumentId: string | null;
  notes: string | null;
}

export interface PreApprovalLetterDto {
  id: string;
  dealId: string;
  party: PreApprovalPartyKind;
  status: PreApprovalLetterStatus;
  generatedAt: string | null;
  generatedByUserId: string | null;
  generatedByDisplayName: string | null;
  sentAt: string | null;
  sentByUserId: string | null;
  sentByDisplayName: string | null;
  receivedSignedAt: string | null;
  receivedSignedByUserId: string | null;
  receivedSignedByDisplayName: string | null;
  linkedDocumentId: string | null;
  notes: string | null;
}

export interface UpdatePreApprovalNotesRequest {
  notes: string | null;
}

export interface LinkPreApprovalDocumentRequest {
  documentId: string;
}

export interface PreApprovalSubmissionRequest {
  dscrPreliminary: number | null;
  suggestedCreditRating: SuggestedCreditRatingPreliminary | null;
  recommendation: CertificationRecommendation | null;
  keyFindings: string | null;
  conditionsText: string | null;
  dealMeetsRequirements: boolean;
  allDocumentsComplete: boolean;
  applicationsInOrder: boolean;
  assignedA1UserId: string;
}

export interface PreApprovalReadinessDto {
  borrowerApplicationReceived: boolean;
  sellerApplicationReceived: boolean;
  borrowerLetterReceivedSigned: boolean;
  sellerLetterReceivedSigned: boolean;
  allRequiredDocumentsCleared: boolean;
  outstandingRequiredDocuments: number;
  isReady: boolean;
  isSubmitted: boolean;
  submittedAt: string | null;
  submittedByUserId: string | null;
  submittedByDisplayName: string | null;
  dscrPreliminary: number | null;
  suggestedCreditRating: SuggestedCreditRatingPreliminary | null;
  recommendation: CertificationRecommendation | null;
  keyFindings: string | null;
  conditionsText: string | null;
  assignedA1UserId: string | null;
  assignedA1DisplayName: string | null;
}

export const PREAPPROVAL_PERMISSIONS = {
  ManageApplications: "DealFlow.PreApproval.ManageApplications",
  IssueLetters: "DealFlow.PreApproval.IssueLetters",
  SubmitToUnderwriting: "DealFlow.PreApproval.SubmitToUnderwriting",
} as const;

// ============================== Stage / sub-state metadata ============

// Drives Kanban column ordering and the swim-lane sort within each
// stage column. Mirrors the seed data in
// 20260903000000_DealPipelineModule_v3_0_CounterpartyFoundation.cs and
// the gating rules in DealStageMachine. Sub-states whose stage is
// terminal (Declined / Withdrawn) are not shown as Kanban lanes — those
// rows live on /deal-flow/pipeline/archive.

export const ACTIVE_STAGES: DealPipelineStage[] = [
  "Intake",
  "PreApproval",
  "Underwriting",
  "Closing",
  "Closed",
];

export const TERMINAL_STAGES: DealPipelineStage[] = ["Declined", "Withdrawn"];

export const SUB_STATES_BY_STAGE: Record<DealPipelineStage, DealSubState[]> = {
  Intake: ["New", "GatheringParties", "IntakeReview", "ReadyForAssignment", "AssignedToB1"],
  // Pre-Approval milestones — four buckets that mirror four of the
  // Pre-Approval page's five sections (Endorsement Records has no
  // sub-state). Order matches the LA's workflow.
  PreApproval: ["Applications", "Letters", "Documents", "ReadyToSubmit"],
  Underwriting: [],
  Closing: [],
  Closed: [],
  Declined: ["DeclinedFinal"],
  Withdrawn: ["WithdrawnFinal"],
};

export const STAGE_LABEL: Record<DealPipelineStage, string> = {
  Intake: "Intake",
  PreApproval: "Pre-Approval",
  Underwriting: "Underwriting",
  Closing: "Closing",
  Closed: "Closed",
  Declined: "Declined",
  Withdrawn: "Withdrawn",
};

export const SUB_STATE_LABEL: Record<DealSubState, string> = {
  New: "New",
  GatheringParties: "Gathering Parties",
  IntakeReview: "Intake Review",
  ReadyForAssignment: "Ready for Assignment",
  AssignedToB1: "Assigned to B1",
  Applications: "Applications",
  Letters: "Letters",
  Documents: "Documents",
  ReadyToSubmit: "Ready to Submit",
  AssignedToA1: "Assigned to A1",
  DeclinedFinal: "Declined",
  WithdrawnFinal: "Withdrawn",
  ArchivedFinal: "Archived",
};

/**
 * The sub-state to show beside a deal's stage, or null when the stage has
 * none. Underwriting §3.1: Closing has no sub-states, yet the hand-off leaves
 * the deal's sub-state at AssignedToA1 (and Closed keeps it), so showing it
 * read "Closing → Assigned to A1" (staging run, 2026-10-14).
 */
export function subStateLabelFor(
  stage: DealPipelineStage,
  subState: DealSubState,
): string | null {
  if (stage === "Closing" || stage === "Closed") return null;
  return SUB_STATE_LABEL[subState] ?? null;
}

// ============================== Broker Fee Authorization ==============
// The broker's compensation on a deal: a recurring monthly fee (the only
// money-driving component), an informational one-time cash-at-closing
// amount (paid seller→broker directly, not Phoenix-administered), and a
// dormant investor-phase sale fee. Staff (loan administrators) view, edit,
// and confirm these terms; any edit resets the confirmation server-side.
//
// The cash at closing is a flat amount OR a percentage of the buyer's down
// payment. It is the BROKER's cash at closing — NOT `Deal.cashAtClosing`,
// which is the buyer's §3 contribution. Two concepts, near-identical names.

export type BrokerFeeMonthlyMode = "none" | "percent_of_seller_share" | "flat_monthly";

/**
 * How the BROKER's one-time cash at closing is expressed. Deliberately its own
 * vocabulary and NOT `BrokerFeeMonthlyMode`: the monthly mode sits in the chain
 * that decides whether a broker is payable at all, while cash at closing is a
 * side payment made seller → broker off Phoenix's rails and gates nothing.
 * `none` is an explicit choice, not an empty box.
 */
export type BrokerCashAtClosingMode = "none" | "flat" | "percent_of_down_payment";

export interface BrokerFeeAuthorizationDto {
  dealId: string;
  brokerId: string;
  brokerName: string | null;
  monthlyFeeMode: BrokerFeeMonthlyMode;
  /** FRACTION (0.05 = 5%) in percent mode; flat $/month in flat mode; 0 for none. */
  monthlyFeeAmount: number;
  /** One-time $ in `flat` mode; null otherwise. */
  cashAtClosingAmount: number | null;
  /** FRACTION (0.02 = 2%); dormant/investor-phase. */
  saleFeePercent: number | null;
  isVerified: boolean;
  verifiedAt: string | null;
  verifiedByUserId: string | null;
  verifiedByName: string | null;
  cashAtClosingMode: BrokerCashAtClosingMode;
  /** FRACTION (0.10 = 10%) of `downPaymentBasisAmount`; only in percent mode. */
  cashAtClosingPercent: number | null;
  /** The down payment the percentage resolves against. */
  downPaymentBasisAmount: number | null;
  /**
   * Server-computed dollars: 0 for `none`, the amount for `flat`, basis ×
   * percent for `percent_of_down_payment` — and NULL when the percentage has
   * nothing to resolve against. Never compute this on the client.
   */
  resolvedCashAtClosingAmount: number | null;
  /** The deal's current `DealIntakeProfile.DownPaymentAmount` — for prefill and drift. */
  dealDownPaymentAmount: number | null;
  /** Mirrors the server's confirm-time gate; false ⇒ `verifyBlockedReason` says why. */
  canVerify: boolean;
  verifyBlockedReason: string | null;
}

export interface UpdateBrokerFeeAuthorizationRequest {
  monthlyFeeMode: BrokerFeeMonthlyMode;
  /** FRACTION in percent mode, $ in flat mode. */
  monthlyFeeAmount: number;
  cashAtClosingAmount: number | null;
  saleFeePercent: number | null;
  cashAtClosingMode: BrokerCashAtClosingMode;
  /** FRACTION (0.10 = 10%) — the UI works in whole percent and divides on save. */
  cashAtClosingPercent: number | null;
  downPaymentBasisAmount: number | null;
}

// ============================== Buyer Consulting Fee Authorization ====
// Phoenix's own consulting fee on a deal, in two INDEPENDENT legs:
//
//   • monthly  — a percentage of each scheduled payment of principal and
//                interest. Never applies to the balloon; the liquidity leg
//                bills that instead.
//   • liquidity — a percentage of the gross the buyer remits to retire or
//                accelerate the loan (scheduled balloon, early payoff,
//                refinance, full voluntary prepayment).
//
// Both are CARVES, not charges on top: the buyer's payment amount does not
// change and the seller's distribution is smaller by exactly the fee.
//
// 🔴 There is no mode member and there must never be one — each leg is on or
// off by being non-zero or zero, and either may be zero while the other is
// not. Do not model this on `BrokerFeeMonthlyMode`.

/**
 * Mirrors `BuyerConsultingFeeAuthorizationDto` (a positional C# record — new
 * members go on the END). Unlike `BrokerFeeAuthorizationDto` there is no
 * `canVerify` / `verifyBlockedReason` pair: two bare percentages are always
 * resolvable, so nothing can block confirmation.
 */
export interface BuyerConsultingFeeAuthorizationDto {
  dealId: string;
  /** FRACTION (0.025 = 2.5%) of the scheduled P&I payment. */
  monthlyFeePct: number;
  /** FRACTION (0.025 = 2.5%) of the gross remitted at a liquidity event. */
  liquidityFeePct: number;
  isVerified: boolean;
  verifiedAt: string | null;
  verifiedByUserId: string | null;
  verifiedByName: string | null;
  /**
   * The server's per-leg cap as a FRACTION (0.15 = 15%), projected from
   * `BuyerConsultingFeeCodes.MaxRateFraction` so the form states — and
   * enforces — the same bound the server does instead of hardcoding it a
   * second time. Capped independently per leg; there is no combined cap.
   */
  maxRateFraction: number;
}

export interface UpdateBuyerConsultingFeeAuthorizationRequest {
  /** FRACTION — the UI works in whole percent and divides on save. */
  monthlyFeePct: number;
  /** FRACTION — the UI works in whole percent and divides on save. */
  liquidityFeePct: number;
}
